import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import Controller from '@ember/controller';
import { task } from 'ember-concurrency';
import { TwoFactorAuthRequiredError } from 'cio/authenticators/login-2fa';
import { TwoFactorAuthLinkRequiredError } from 'cio/authenticators/login-2fa-link';
import { setTwoFactorAuthLinkCookie } from 'cio/utils/auth-cookies';

import AlertsService from 'cio/services/alerts';
import RouterService from '@ember/routing/router-service';
import SsoConfiguration, { IdentityProvider } from 'cio/models/sso-configuration';
import {
  ChooseUserEntryDataObject,
  GoogleOneClickMustChooseUserAccountError,
} from 'cio/authenticators/login-google-one-click';
import AnalyticsService from 'cio/services/analytics';
import LoginService, { LoginError, LoginErrorType } from 'cio/services/login';

export type LoginMeta = {
  ga_client_id: string;
};

export default class LoginController extends Controller {
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service router!: RouterService;
  @service login!: LoginService;

  email = '';
  password = '';
  twoFactorAuthCode = '';
  twoFactorAuthLoginToken = '';
  ssoProvider = '';
  googleOneClickChooseUserEntries!: null | ChooseUserEntryDataObject[];
  googleOneClickChooseUserJWTToken = '';
  googleOneClickGoogleAccountEmail = '';
  twoFactorAuthLinkToken = '';
  logoWithNameImage = new URL('cio/assets/images/logo-with-name.svg', import.meta.url).toString();
  greetingAnimationImage = new URL('cio/assets/images/greeting-animation.svg', import.meta.url).toString();

  ssoConfigurationForLogin!: SsoConfiguration;
  userExternalIdentityEmail = '';

  @computed('router.currentRouteName')
  get isLoginIndexRoute() {
    return this.router.isActive('login.index');
  }

  loginTaskEmailPassword = task(async () => {
    return this.login.emailPassword.perform(this.email, this.password).catch((err) => {
      this.handleError(err);
    });
  });

  loginTaskTwoFactorAuthLink = task(async () => {
    return this.login.twoFactorAuthLink.perform(this.twoFactorAuthLinkToken).catch((err) => {
      this.handleError(err);
    });
  });

  loginTaskSSO = task(async () => {
    let { ssoConfigurationForLogin, email, userExternalIdentityEmail } = this;
    let { identityProvider } = ssoConfigurationForLogin;

    if (identityProvider === IdentityProvider.GoogleOIDC) {
      return this.login.SSOGoogleOIDC.perform({
        email,
        ssoConfigurationForLogin,
        userExternalIdentityEmail,
      }).catch((err) => this.handleError(err));
    } else if (identityProvider === IdentityProvider.OktaOIDC) {
      return this.login.SSOOktaOIDC.perform({
        email,
        ssoConfigurationForLogin,
      }).catch((err) => this.handleError(err));
    } else if (identityProvider === IdentityProvider.AzureOIDC) {
      return this.login.SSOAzureOIDC.perform({
        email,
        ssoConfigurationForLogin,
      }).catch((err) => this.handleError(err));
    } else if (identityProvider === IdentityProvider.GenericOIDC) {
      return this.login.SSOGenericOIDC.perform({
        email,
        ssoConfigurationForLogin,
      }).catch((err) => this.handleError(err));
    }

    return;
  });

  loginTaskGoogleOneClick = task(async () => {
    return this.login.googleOneClick.perform().catch((err) => {
      if (err instanceof GoogleOneClickMustChooseUserAccountError) {
        // Google One-Click login: more than one CIO accounts to select from, show account chooser
        this.setProperties({
          googleOneClickChooseUserEntries: err.usersData,
          googleOneClickChooseUserJWTToken: err.chooseUserJWTToken,
          googleOneClickGoogleAccountEmail: err.googleAccountEmail,
        });
        this.router.transitionTo('login.choose_user');
      } else {
        this.handleError(err);
      }
    });
  });

  loginTaskGoogleOneClickChooseUserFinish = task(async (userID: string) => {
    return this.login.googleOneClickChooseUserFinish
      .perform({ userID, googleOneClickChooseUserJWTToken: this.googleOneClickChooseUserJWTToken })
      .catch((err) => this.handleError(err));
  });

  loginTaskTwoFactorAuth = task(async () => {
    return this.login.twoFactorAuth
      .perform({ twoFactorAuthCode: this.twoFactorAuthCode, twoFactorAuthLoginToken: this.twoFactorAuthLoginToken })
      .catch((err) => this.handleError(err));
  });

  private handleError(err: LoginError | TwoFactorAuthRequiredError | TwoFactorAuthLinkRequiredError) {
    if (err instanceof TwoFactorAuthRequiredError) {
      this.set('twoFactorAuthLoginToken', err.twoFactorAuthLoginToken);
      this.router.transitionTo('login.two_factor_authentication');
    } else if (err instanceof TwoFactorAuthLinkRequiredError) {
      setTwoFactorAuthLinkCookie(err.twoFactorAuthLinkToken);
      this.router.transitionTo('login.2fa_email_link');
    } else if (err instanceof LoginError) {
      if (err.type === LoginErrorType.TWO_FACTOR_INVALID_CODE || err.type === LoginErrorType.TWO_FACTOR_USED_CODE) {
        this.set('twoFactorAuthCode', '');
      }
      this.alerts.error('login.error', err.message);
    }
  }

  setDefaults() {
    this.setProperties({
      email: '',
      password: '',
      twoFactorAuthCode: '',
      twoFactorAuthLoginToken: '',
    });
  }

  @action useDifferentEmail() {
    this.setDefaults();

    this.analytics.track('user', 'sign_in_with_different_email_clicked');

    return this.router.transitionTo('login.index');
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    login: LoginController;
  }
}
